// @flow strict-local

import type {
  MessageNode,
  EditableGraph,
  Id,
  FileUploadNode,
} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useEditorState} from './reducer';
import {findAncestorNodeVariables} from '../utils';
import {validateNode} from './validation';

import EditorContainer from './container.jsx';
import {MessageDraftEditor} from 'src/components/conversation-builder/draft-editor.jsx';
import {PadSection, useFirstSmsMessage} from './common.jsx';


export default function MessageEditor({
  graph,
  defaultNode,
  entityType,
  hasSubmitted,
  edgeId,
  ...props
}: {
  graph: EditableGraph,
  entityType: EntityType,
  onClose: () => mixed,
  onSubmit: (MessageNode | FileUploadNode) => mixed,
  defaultNode: MessageNode | FileUploadNode,
  actionName: 'add' | 'edit',
  hasSubmitted: boolean,
  edgeId?: Id,
  readOnly: mixed,
}): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const isFirstSmsMessage = useFirstSmsMessage(graph, defaultNode);

  // $FlowFixMe incompatiable-call props-missing (Flow will not allow multi line error codes. Adding props-missing because this component will be deprecated, and the new file upload writeback feature won't be supported here.)
  const [state, dispatch] = useEditorState({defaultNode});

  const update = (payload) => dispatch({type: 'update', payload});
  const {node} = state;

  const error = hasSubmitted ? validateNode(node, graph) : null;

  const nodeVariables = findAncestorNodeVariables(graph, defaultNode, edgeId);

  return (
    <EditorContainer onUpdate={update} state={state} error={error} {...props}>
      <PadSection>
        <MessageDraftEditor
          label={t('Message', 'Message')}
          placeholder={`${t('Enter_a_question', 'Enter a question')}...`}
          defaultValue={node.text_draft_json}
          onChange={(text_draft_json) => update({text_draft_json})}
          entityType={entityType}
          isFirstSmsMessage={isFirstSmsMessage}
          error={error?.text_draft_json}
          readOnly={Boolean(props.readOnly)}
          useCase={graph.use_case}
          nodeVariables={nodeVariables}
        />
      </PadSection>
    </EditorContainer>
  );
}
